/**
 * Tiered free gifts — adds and removes the gift line for the native mini cart
 *
 * One gift per tier reached, each strictly quantity 1. Reaching tier 2 adds its
 * gift and keeps tier 1's; dropping below a tier removes only that tier's gift.
 *
 * Tiers are the Gift blocks of the mini-cart section (threshold, product, "Add
 * gift automatically"). Free shipping tiers are display only and never touch
 * the cart.
 *
 * Gift lines show in the drawer and on the /cart page with no quantity, bin or
 * link (sections/mini-cart.liquid, sections/main-cart-items.liquid).
 *
 * No reads on the normal path. Every redraw of the drawer prints the tiers and
 * the cart state this needs into #free-gift-data, then the theme dispatches
 * 'cart:updated' (PROJ.ajaxcart.updateCart in theme-bescher.js). Only checkout
 * reads the cart fresh.
 *
 * Also home to the ONE Checkout check, for the drawer and the /cart page. It
 * fixes the tier gift and the Welcome Kit (welcome-kit-cart.js) together, then
 * goes to checkout. See "checkout" below.
 *
 * Also removes leftover hidden lines once nothing visible is in the cart, and
 * saves Shopify's exchange rate for the drawer in other currencies (see
 * rateToSave).
 *
 * Debug: localStorage.setItem('free_gift_debug', '1')
 */
(function () {
  'use strict';

  /* Not _free_gift or _monster_free_gift: Monster Cart uses both. */
  var MARKER_KEY = '_tier_gift';

  /* Let a burst of changes settle before writing. */
  var QUIET_MS = 250;
  /* One retry covers losing a race for the cart lock (HTTP 422). */
  var RETRY_MS = 1200;
  /* Never hold a customer at checkout longer than this. */
  var CHECKOUT_TIMEOUT_MS = 2500;
  /* Stop rather than join a runaway involving another script. */
  var MAX_WRITES = 6;
  var WRITE_WINDOW_MS = 10000;

  var nativeFetch = window.fetch ? window.fetch.bind(window) : null;

  var startedAt = Date.now();
  var timer = null;
  var running = false;
  var queued = false;
  var stopped = false;
  var retried = false;
  var writeLog = [];
  /* The change in progress, so checkout can wait for it to land. */
  var active = null;
  /* Set while the Checkout check works: no other change may start. */
  var checkingOut = false;

  function debug() {
    try {
      if (window.localStorage.getItem('free_gift_debug') !== '1') return;
      var at = '+' + ((Date.now() - startedAt) / 1000).toFixed(2) + 's';
      console.log.apply(console, ['[free-gift ' + at + ']'].concat(Array.prototype.slice.call(arguments)));
    } catch (e) {
      /* logging must never break the cart */
    }
  }

  /* Monster Cart runs its own gifts and does not redraw the native drawer, so
     the data below would be stale. Hands off while it is on the page. */
  function monsterActive() {
    /* Matches cart_renderer.<hash>.js and cart_renderer_v2.js alike. */
    return !!document.querySelector('script[src*="/cart_renderer"]');
  }

  /* What the last drawer render printed. Null when there is no drawer. */
  function readData() {
    var el = document.getElementById('free-gift-data');
    if (!el) return null;

    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      debug('could not read #free-gift-data', e && e.message);
      return null;
    }
  }

  function overBudget() {
    var now = Date.now();

    writeLog = writeLog.filter(function (at) {
      return now - at < WRITE_WINDOW_MS;
    });

    if (writeLog.length < MAX_WRITES) return false;

    stopped = true;

    try {
      console.warn('[free-gift] too many cart writes in a row — stopping. Reload to resume.');
    } catch (e) {
      /* no-op */
    }

    return true;
  }

  /* --- the cart ----------------------------------------------------------- */

  function getCart() {
    var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';

    return nativeFetch(root + 'cart.js', {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    }).then(function (response) {
      return response.json();
    });
  }

  /* /cart.js in the same shape the drawer prints. Gift lines are left out of
     the total, as in the drawer, so a gift never counts toward its own tier.
     hidden = free_gift products. The Welcome Kit counts as neither:
     welcome-kit-cart.js removes it itself. */
  function fromCart(cart) {
    var items = (cart && cart.items) || [];
    var state = { total: 0, visible: 0, hidden: [], gifts: [] };

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var properties = item.properties || {};

      if (properties[MARKER_KEY]) {
        state.gifts.push({ key: item.key, variantId: item.variant_id, quantity: item.quantity });
        continue;
      }

      state.total += item.final_line_price;

      if (properties._welcome_kit === 'bescher') continue;

      if (item.product_type === 'free_gift') state.hidden.push(item.key);
      else state.visible++;
    }

    return state;
  }

  function post(path, body) {
    debug('POST', path, body);

    return nativeFetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }).then(function (response) {
      if (response.ok) return response.json();

      return response.text().then(function (text) {
        debug('POST ' + path + ' rejected — HTTP ' + response.status, text);
        var error = new Error('Free gift: ' + path + ' failed (' + response.status + ')');
        error.status = response.status;
        throw error;
      });
    });
  }

  /* Sequential: the cart API does not take concurrent writes. */
  function apply(operations) {
    return operations.reduce(function (chain, operation) {
      return chain.then(function () {
        if (operation.add) return post('/cart/add.js', { items: [operation.add] });
        if (operation.attributes) return post('/cart/update.js', { attributes: operation.attributes });
        if (operation.note !== undefined) return post('/cart/update.js', { note: operation.note });
        return post('/cart/change.js', { id: operation.id, quantity: operation.quantity });
      });
    }, Promise.resolve());
  }

  /* Redraws without opening the drawer. The redraw prints fresh data and
     fires 'cart:updated', which brings us back here to find nothing to do. */
  function refreshDrawer() {
    try {
      if (window.PROJ && window.PROJ.ajaxcart) window.PROJ.ajaxcart.updateCart();
    } catch (e) {
      /* never let a drawer redraw break cart state */
    }
  }

  /* --- other currencies ----------------------------------------------------
     Thresholds are typed in the store currency. In any other currency the
     drawer multiplies them by Shopify's exchange rate, as Monster did. Only
     this script can see that rate (Shopify.currency, set on every page), so it
     saves it in the hidden cart attribute _fx_rate for the drawer to read.
     Returns the value to save, or null when the saved one is current. */
  function rateToSave(data) {
    var currency = window.Shopify && window.Shopify.currency;

    if (!data.fxNeeded) return null;
    if (!currency || currency.active !== data.currency || !(Number(currency.rate) > 0)) return null;

    var value = currency.active + ':' + currency.rate;
    return data.fx === value ? null : value;
  }

  /* --- what the cart should look like -------------------------------------- */

  /* Every tier reached, lowest first. The drawer only lists tiers that can give
     a gift (switched on, auto-add ticked, product in stock), so a sold-out gift
     is simply skipped. */
  function targetTiers(tiers, total) {
    return tiers
      .filter(function (tier) {
        return total >= tier.threshold;
      })
      .sort(function (a, b) {
        return a.threshold - b.threshold;
      });
  }

  /* Returns the operations needed, empty when the cart is already correct.
     Removals go first, then quantities back to 1, then the gifts missing. */
  function plan(tiers, state) {
    var removals = [];
    var others = [];

    /* Nothing visible left: tier gifts and free_gift products go. The drawer
       shows an empty cart, so the customer has no way to remove them. The
       Welcome Kit is left to welcome-kit-cart.js. */
    if (state.visible === 0) {
      for (var h = 0; h < state.gifts.length; h++) removals.push({ id: state.gifts[h].key, quantity: 0 });
      for (var k = 0; k < (state.hidden || []).length; k++) removals.push({ id: state.hidden[k], quantity: 0 });
      return removals;
    }

    /* Tiers unknown (exchange rate not saved yet): leave the gift alone. */
    if (!tiers) return removals;

    var targets = targetTiers(tiers, state.total);
    /* Each tier claims one gift line of its product; whatever is left over
       (tier dropped below or switched off, a duplicate) is removed. Matched by
       product, not by the tier label, so changing a threshold never swaps a
       gift out and back in. */
    var unclaimed = state.gifts.slice();
    var missing = [];

    for (var t = 0; t < targets.length; t++) {
      var line = null;

      for (var i = 0; i < unclaimed.length; i++) {
        if (String(unclaimed[i].variantId) === String(targets[t].variantId)) {
          line = unclaimed.splice(i, 1)[0];
          break;
        }
      }

      /* By key, never by id: two lines can share a variant id. */
      if (!line) missing.push(targets[t]);
      else if (line.quantity !== 1) others.push({ id: line.key, quantity: 1 });
    }

    for (var u = 0; u < unclaimed.length; u++) removals.push({ id: unclaimed[u].key, quantity: 0 });

    for (var m = 0; m < missing.length; m++) {
      var properties = {};
      properties[MARKER_KEY] = missing[m].label;
      others.push({ add: { id: missing[m].variantId, quantity: 1, properties: properties } });
    }

    return removals.concat(others);
  }

  function reconcile() {
    if (!nativeFetch || stopped || checkingOut || monsterActive()) return Promise.resolve();

    if (running) {
      queued = true;
      return Promise.resolve();
    }

    var data = readData();
    if (!data) return Promise.resolve();

    /* The rate first: the redraw that follows prints the converted tiers. */
    var rate = rateToSave(data);
    var operations = rate ? [{ attributes: { _fx_rate: rate } }] : plan(data.fxPending ? null : data.tiers, data);

    if (!operations.length) {
      debug('cart already correct, nothing to do');
      retried = false;
      return Promise.resolve();
    }

    if (overBudget()) return Promise.resolve();

    running = true;
    writeLog.push(Date.now());
    debug('applying', operations);

    active = apply(operations)
      .then(refreshDrawer)
      .catch(function (error) {
        debug('reconcile failed', error && error.message);

        if (error && error.status === 422 && !retried) {
          retried = true;
          debug('cart was busy — retrying once in ' + RETRY_MS + 'ms');
          setTimeout(reconcile, RETRY_MS);
          return;
        }

        /* Whatever went wrong, the drawer's data may be out of date — e.g. a
           line already gone. A redraw brings it back in line. */
        refreshDrawer();
      })
      .then(function () {
        running = false;
        active = null;

        if (queued) {
          queued = false;
          schedule();
        }
      });

    return active;
  }

  function schedule() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(reconcile, QUIET_MS);
  }

  /* --- checkout ------------------------------------------------------------
     ONE check for Checkout, in the drawer and on the /cart page. It fixes the
     tier gift and the Welcome Kit together, then goes to checkout. It always
     runs: with the kit or the gifts switched off (or both) it still puts the
     cart right, as their plans then remove what no longer belongs.

     Why one: the kit used to hold the click, check, and click the button
     again, and this check followed on the form's submit. When the drawer
     redrew in between, the button clicked was no longer on the page, nothing
     happened, and the customer had to click Checkout twice.

       1. Spinner on; further clicks are ignored.
       2. Both scripts stop starting changes, and what is already on its way
          is waited for, so the read below cannot miss it.
       3. One fresh read, one list of fixes (the kit's, then the gift's),
          applied in order. A fix that fails — e.g. a line already gone — is
          tried once more from a fresh read.
       4. Straight to /checkout, not by submitting a form: a redraw can replace
          the drawer's form, and after a fix the /cart page's quantity fields
          no longer match the cart. Its order note is saved in step 3.

     Never stuck: after CHECKOUT_TIMEOUT_MS the customer goes to checkout
     whatever is still going on. If the page somehow does not move on,
     everything comes back after RESUME_MS. */
  var CHECKOUT_BUTTONS = '#mini-cart .chek-btn, #checkout';
  var RESUME_MS = 10000;

  /* welcome-kit-cart.js, when it is on the page. */
  function kitCheckout() {
    var kit = window.BescherWelcomeKitCheckout;
    return kit && typeof kit.plan === 'function' ? kit : null;
  }

  function spinCheckout(on) {
    var buttons = document.querySelectorAll(CHECKOUT_BUTTONS);

    for (var i = 0; i < buttons.length; i++) {
      buttons[i].classList.toggle('is-loading', on);
      if (on) buttons[i].setAttribute('aria-busy', 'true');
      else buttons[i].removeAttribute('aria-busy');
    }
  }

  /* The fixes the cart needs before checkout: the kit's, the gift's, and the
     /cart page's order note when it differs (note is null elsewhere). */
  function checkoutPlan(cart, note) {
    var operations = [];
    var kit = kitCheckout();
    var data = readData();

    if (kit) operations = operations.concat(kit.plan(cart));
    if (data && !monsterActive()) operations = operations.concat(plan(data.fxPending ? null : data.tiers, fromCart(cart)));
    if (note !== null && note !== (cart.note || '')) operations.push({ note: note });

    return operations;
  }

  function resumeAfterCheckout() {
    var kit = kitCheckout();

    checkingOut = false;
    spinCheckout(false);
    if (kit) kit.pause(false);
    schedule();
  }

  function checkout(note) {
    if (checkingOut) return;

    var kit = kitCheckout();
    var gone = false;

    checkingOut = true;
    if (timer) clearTimeout(timer);
    if (kit) kit.pause(true);
    spinCheckout(true);
    debug('checkout: checking the cart first');

    function go() {
      if (gone) return;
      gone = true;
      debug('checkout: on our way');

      var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
      window.location.href = root + 'checkout';
      setTimeout(resumeAfterCheckout, RESUME_MS);
    }

    function verify(lastTry) {
      return getCart()
        .then(function (cart) {
          var operations = checkoutPlan(cart, note);
          if (operations.length) debug('checkout: fixing the cart first', operations);
          return apply(operations);
        })
        .catch(function (error) {
          if (lastTry) throw error;
          debug('checkout: a fix failed, once more from a fresh read', error && error.message);
          return verify(true);
        });
    }

    setTimeout(go, CHECKOUT_TIMEOUT_MS);

    try {
      if (!nativeFetch) return go();

      Promise.all([active, kit ? kit.idle() : null])
        .then(function () {
          return verify(false);
        })
        .then(go, go);
    } catch (e) {
      go();
    }
  }

  /* On window in the capture phase: before anything else on the page sees the
     click. preventDefault keeps the form from submitting; the click itself
     still reaches other listeners (analytics). */
  window.addEventListener(
    'click',
    function (event) {
      var button;

      try {
        button = event.target && event.target.closest ? event.target.closest(CHECKOUT_BUTTONS) : null;
      } catch (e) {
        return;
      }

      if (!button) return;

      event.preventDefault();

      var note = null;

      /* /cart page: its order note saves itself a moment after typing; take
         the field as it is now, in case that has not happened yet. */
      if (button.id === 'checkout') {
        var field = document.querySelector('cart-note [name="note"], #cart [name="note"], [name="note"][form="cart"]');
        if (field) note = field.value;
      }

      checkout(note);
    },
    true
  );

  /* Enter in a drawer quantity field submits the drawer's form, which goes to
     checkout too: same check. */
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;

      if (!form || !form.matches || !form.matches('#mini-cart form.minicart-form')) return;

      event.preventDefault();
      checkout(null);
    },
    true
  );

  /* Back button from checkout: the page can come back from the browser's
     cache mid-check, spinner on and both scripts paused. */
  window.addEventListener('pageshow', function (event) {
    if (event.persisted && checkingOut) resumeAfterCheckout();
  });

  /* --- the /cart page -------------------------------------------------------
     The page (Dawn's cart.js) shows the cart as it was when it was drawn, and
     knows nothing of the drawer. Two links keep the two in step:

       1. A change made on the page redraws the drawer, so both scripts follow
          it exactly as they follow a change in the drawer.
       2. When the drawer shows a different cart from the page — a gift or the
          kit added or removed — the page's items and totals are redrawn.

     It cannot loop. Only a change the customer makes on the page publishes
     'cart-items' (cart.js, after a quantity change or remove), and the redraw
     in 2 publishes nothing and changes nothing: it only reads. pageLines is
     the cart the page shows, so each cart is drawn at most once, and a newer
     change on the page cancels a redraw still on its way (pageRun). The cart
     itself is only ever changed by reconcile(), with its own write limit.

     cart.js changes lines by key, so even a page that is a moment behind
     never changes the wrong product. */
  var pageLines = null;
  var pageRun = 0;

  /* Dawn's <cart-items>, once cart.js has set it up. Null on other pages. */
  function cartPage() {
    var page = document.querySelector('cart-items');
    return page && typeof page.getSectionsToRender === 'function' ? page : null;
  }

  function linesOf(items) {
    return (items || [])
      .map(function (item) {
        return item.key + ':' + item.quantity;
      })
      .join(',');
  }

  function refreshCartPage() {
    var page = cartPage();
    var data = readData();
    var sections;

    if (!nativeFetch || !page || !data || typeof data.lines !== 'string' || data.lines === pageLines) return;

    try {
      sections = page.getSectionsToRender().filter(function (section) {
        return document.getElementById(section.id);
      });
    } catch (e) {
      return;
    }

    var run = ++pageRun;
    var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    var ids = sections.map(function (section) {
      return section.section;
    });

    pageLines = data.lines;
    debug('/cart page shows an older cart — redrawing it');

    /* No Accept: application/json here. With it Shopify ignores ?sections and
       answers with the cart itself, as /cart.js does. */
    nativeFetch(root + 'cart?sections=' + encodeURIComponent(ids.join(',')), {
      method: 'GET',
      credentials: 'same-origin',
    })
      .then(function (response) {
        if (!response.ok) throw new Error('/cart sections failed (' + response.status + ')');
        return response.json();
      })
      .then(function (html) {
        if (run !== pageRun) return;

        if (!html || !ids.some(function (id) { return html[id]; })) throw new Error('/cart sections missing from the answer');

        /* As cart.js does after its own changes. */
        sections.forEach(function (section) {
          if (!html[section.section]) return;
          var target = document.getElementById(section.id);
          (target.querySelector(section.selector) || target).innerHTML = page.getSectionInnerHTML(
            html[section.section],
            section.selector
          );
        });

        var items = sections.filter(function (section) {
          return section.id === 'main-cart-items';
        })[0];

        if (items && html[items.section]) {
          var empty = !!new DOMParser().parseFromString(html[items.section], 'text/html').querySelector('cart-items.is-empty');
          var footer = document.getElementById('main-cart-footer');
          page.classList.toggle('is-empty', empty);
          if (footer) footer.classList.toggle('is-empty', empty);
        }
      })
      .catch(function (error) {
        debug('/cart page redraw failed', error && error.message);
        /* Try again on the next drawer redraw. */
        if (run === pageRun) pageLines = null;
      });
  }

  /* Link 1. subscribe and PUB_SUB_EVENTS come from pubsub.js and constants.js,
     loaded before this file. */
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
      if (!event || event.source !== 'cart-items' || !cartPage()) return;

      /* cart.js has just drawn the page from this cart; anything on its way is older. */
      pageRun++;
      if (event.cartData && event.cartData.items) pageLines = linesOf(event.cartData.items);

      debug('changed on the /cart page — redrawing the drawer');
      refreshDrawer();
    });
  }

  /* The page and the drawer are drawn from the same cart on page load. */
  (function () {
    var data = readData();
    if (data && typeof data.lines === 'string' && document.getElementById('main-cart-items')) pageLines = data.lines;
  })();

  /* --- following the cart -------------------------------------------------- */

  document.addEventListener('cart:updated', function () {
    /* A redraw during the Checkout check replaces the button: spinner back on. */
    if (checkingOut) spinCheckout(true);
    debug('drawer redrawn');
    /* Link 2. */
    refreshCartPage();
    schedule();
  });

  /* Page load: the drawer is rendered with the page, so its data is already
     there. Covers a cart changed elsewhere, or a tier switched on or off. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }

  /* For manual checks in the console. */
  window.BescherFreeGiftReconcile = reconcile;
})();
